import React from 'react';

const NEWBIT_WS_URL = 'wss://newbitex.com/wss/msg';
const NB_SYMBOL = 'NB/USDT';
const RECONNECT_DELAY = 5_000;

export interface NewbitTicker {
  symbol: string;
  lastPrice: string;
  lastChangeRate: string;
  high: string;
  low: string;
  last24hVol: string;
  last24hQuoteVol: string;
}

interface NewbitWsMessage {
  event?: string;
  data?: Array<NewbitTicker>;
}

interface Snapshot {
  ticker: NewbitTicker | undefined;
  isError: boolean;
}

// single shared websocket connection for all consumers (top bar, chain indicators, market block)
let ws: WebSocket | undefined;
let reconnectTimer: number | undefined;
let snapshot: Snapshot = { ticker: undefined, isError: false };
const listeners = new Set<() => void>();

const SERVER_SNAPSHOT: Snapshot = { ticker: undefined, isError: false };

function notify() {
  listeners.forEach((listener) => listener());
}

function connect() {
  ws = new WebSocket(NEWBIT_WS_URL);

  ws.onopen = () => {
    ws?.send(JSON.stringify({ method: 'SUBSCRIBE', event: 'detail' }));
  };

  ws.onmessage = (message) => {
    try {
      const response = JSON.parse(message.data) as NewbitWsMessage;
      if (response.event !== 'detail' || !Array.isArray(response.data)) {
        return;
      }
      const pair = response.data.find((item) => item.symbol === NB_SYMBOL);
      if (pair) {
        snapshot = { ticker: pair, isError: false };
        notify();
      }
    } catch (error) {}
  };

  ws.onerror = () => {
    snapshot = { ...snapshot, isError: true };
    notify();
  };

  ws.onclose = () => {
    ws = undefined;
    if (listeners.size > 0) {
      reconnectTimer = window.setTimeout(connect, RECONNECT_DELAY);
    }
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!ws) {
    window.clearTimeout(reconnectTimer);
    connect();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.clearTimeout(reconnectTimer);
      ws?.close();
      ws = undefined;
    }
  };
}

export default function useNewbitTicker() {
  const { ticker, isError } = React.useSyncExternalStore(subscribe, () => snapshot, () => SERVER_SNAPSHOT);

  return { ticker, isError: isError && !ticker };
}
